// Ultimate Card Game (UCG) - developer A/B switches for performance hypotheses.
//
// Each flag turns ONE unproven optimisation on, so its effect can be measured on a real device
// (Options > GRAPHIC & MORE, or the URL: ?ab=hidegame,freematch  /  ?ab=off clears them all).
// Persisted in localStorage so the PWA keeps them across launches. Off by default: none of these
// is known to help yet; compare the memory probe (js/ui/memProbe.js) and Safari's Energy/Timelines
// with the flag on and off, keep what measurably helps and delete the rest.

export const AB_STORAGE_KEY = 'ucg_ab_flags';

export const AB_FLAGS = Object.freeze({
  hidegame: 'HIDE GAME UNDER CARGO BAY',       // visibility:hidden on the battlefield + HUD while the Cargo Bay is open
  freematch: 'FREE MATCH CANVASES IN MENUS',   // drop the baked background/base canvases when the Cargo Bay opens
  norays: 'NO GOD-RAYS IN PACK TEAR',          // diagnostic: hide the 811 px blurred rays layer of the tear
  novfx: 'NO SPARKS/ARCS IN PACK TEAR',        // diagnostic: skip the canvas sparks and lightning of the tear
  nowarpfx: 'NO WARP FLASH/STREAKS',            // diagnostic: Warp Speed without its flash, speed streaks and card bloom
  nowarpaudio: 'NO WARP FLIP SOUNDS',          // diagnostic: Warp Speed without the per-card flip sounds
});

function readStored(storage) {
  try {
    const raw = storage?.getItem?.(AB_STORAGE_KEY);
    const list = raw ? JSON.parse(raw) : [];
    return Array.isArray(list) ? list.filter((f) => f in AB_FLAGS) : [];
  } catch {
    return [];
  }
}

function writeStored(storage, flags) {
  try {
    if (flags.length) storage?.setItem?.(AB_STORAGE_KEY, JSON.stringify(flags));
    else storage?.removeItem?.(AB_STORAGE_KEY);
  } catch { /* private mode / quota: the switches just do not persist */ }
}

// Resolves the active flags: ?ab=... replaces the stored set (and is persisted), ?ab=off clears it.
export function resolveAbFlags({ search = '', storage = null } = {}) {
  let param = null;
  try { param = new URLSearchParams(search).get('ab'); } catch { param = null; }
  if (param !== null) {
    const flags = param === 'off' ? [] : [...new Set(param.split(',').map((f) => f.trim()).filter((f) => f in AB_FLAGS))];
    writeStored(storage, flags);
    return flags;
  }
  return readStored(storage);
}

let active = new Set();

export function initAbFlags(win = globalThis.window, doc = globalThis.document) {
  let storage = null;
  try { storage = win?.localStorage ?? null; } catch { storage = null; }
  active = new Set(resolveAbFlags({ search: win?.location?.search || '', storage }));
  for (const f of Object.keys(AB_FLAGS)) doc?.documentElement?.classList?.toggle(`ab-${f}`, active.has(f));
  return active;
}

export function activeAbFlags() {
  return [...active];
}

export function isAbOn(name) {
  return active.has(name);
}

export function setAbFlag(name, on, win = globalThis.window, doc = globalThis.document) {
  if (!(name in AB_FLAGS)) return false;
  if (on) active.add(name); else active.delete(name);
  let storage = null;
  try { storage = win?.localStorage ?? null; } catch { storage = null; }
  writeStored(storage, [...active]);
  doc?.documentElement?.classList?.toggle(`ab-${name}`, active.has(name));
  return active.has(name);
}
